import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { secureGet, secureDelete, securePost, secureGetPdf } from "../../config/api";
import { useHistory, useLocation } from "react-router-dom";
import { formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import OptionsMenu from "../../components/OptionsMenu";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import TableLoadingArea from "../../components/TableLoadingArea";
import StatusChain, { STATUS_COLORS as CHAIN_COLORS } from "../../components/StatusChain";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";

import EyeIcon from "../../icons/eye.svg";
import DeleteIcon from "../../icons/trashR.svg";
import DownloadIcon from "../../icons/downloadB.svg";

const Invoices = () => {
  const [, setInvoicesData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientId: null,
    commercialId: null,
    dateRange: {},
    status: [],
    needle: "",
  });
  const [clients, setClients] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const history = useHistory();
  const location = useLocation();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const { state: { role } } = useContext(UserContext);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isCommercial = role === "ROLE_COMMERCIAL";

  useEffect(() => {
    if (location.state?.showSnackbar) {
      console.log("Snackbar message:", location.state.snackbarMessage);
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const renderStatut = (bl) => {
    const quotesExist = bl.carts?.[0]?.quotes?.length > 0;

    return (
      <StatusChain
        items={[
          { key: "DV", label: "DV", color: quotesExist ? CHAIN_COLORS.active : CHAIN_COLORS.missing },
          { key: "BC", label: "BC" },
          { key: "BL", label: "BL" },
          { key: "FA", label: "FA" },
        ]}
      />
    );
  };

  const columns = [
    { label: t(`columns.invoiceNumber`), key: "invoiceNumber" },
    {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    { label: "Client", key: "client", render: (client) => client?.name || "N/A" },
    { label: t(`columns.commercial`), key: "user", render: (user) => `${user.firstName} ${user.lastName}` },
    { label: "Total HT", key: "priceHT" },
    { label: "Total TTC", key: "priceTTC" },
    { label: t("columns.status"), render: (_, rowData) => renderStatut(rowData) },
    {
      key: "actions",
      render: (_, rowData) =>
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
    },
  ];

  const getOptions = (item) => {
    const options = [
      { label: t(`actions.details`), icon: EyeIcon, action: () => history.push({ pathname: `/invoices/details/${item.id}`, state: { filters, page: currentPage, size: pageSize } }) },
      { label: t(`actions.downloadInvoice`), icon: DownloadIcon, action: () => handleDownload(item) },
      { label: t(`actions.delete`), color: '#EF466F', icon: DeleteIcon, action: () => handleDelete(item) },
    ];
    return options;
  };

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
    },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: [...clients.map((client) => ({
        value: client.id,
        reference: client.code,
        name: client.name
      }))],
    },
    {
      name: "commercialId",
      label: t(`filters.orders.commercial`),
      type: "dropdown",
      options: isCommercial
        ? [
          {
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          },
        ]
        : commercials.map((commercial) => ({
          value: commercial.id,
          label: `${commercial.firstName} ${commercial.lastName}`,
        })),
    }
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const clientResponse = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientResponse.data);

        if (!isCommercial) {
        const commercialResponse = await secureGet("/users/ROLE_COMMERCIAL", showSnackbar, { signal });
        setCommercials(commercialResponse.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };
    fetchDropdownOptions();
    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchPaginatedInvoices = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);
    try {
      const requestBody = {
        clientId: activeFilters.clientId || null,
        commercialId: activeFilters.commercialId || null,
        startDate: activeFilters.startDate || null,
        endDate: activeFilters.endDate || null,
        status: activeFilters.status.length > 0 ? activeFilters.status : null,
        needle: activeFilters.needle || null,
      };

      const response = await securePost(`/invoices/all/paged`, requestBody, showSnackbar, {
        params: { page, size },
        signal,
      });

      const { content, totalPages } = response.data;
      setInvoicesData(content);
      console.log("Invoices data:", content);
      setCurrentPageData(content);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching invoices:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal]);

  useEffect(() => {
    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);

      fetchPaginatedInvoices(restoredPage, restoredSize, restoredFilters);
    } else {
      fetchPaginatedInvoices(0, pageSize, filters);
    }
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedInvoices(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedInvoices(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else if (filterName === "status") {
      updatedFilters.status = filterValue;
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedInvoices(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      clientId: null,
      commercialId: null,
      dateRange: {},
      status: [],
      needle: "",
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedInvoices(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await secureDelete(`/invoices/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaginatedInvoices(currentPage, pageSize);
      showSnackbar(t(`confirmation.invoiceDelete`), 'success');
    } catch (error) {
      console.error("Error deleting invoice:", error);
      setDeletePopupVisible(false);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/invoices/details/${item.id}`,
      state: {
        filters,
        page: currentPage,
        size: pageSize,
      },
    });
  };


  const handleDownload = async (item) => {
    try {
      const response = await secureGetPdf(`/invoices/${item.id}/pdf`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${item.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download invoice:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.invoices")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.invoices`)}</h1>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <TableLoadingArea loading={loading}>
        <Table
          columns={columns}
          data={currentPageData}
          onRowClick={(row) => handleDetails(row)}
        />
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Invoices;